/**
 * Настройки наблюдения за столбцом, доской или проектом: какие события
 * присылать. Тот же диалог оформляет новую подписку («Наблюдать») и правит
 * действующую — с кнопкой «Прекратить наблюдение».
 */

import type { WatchEvent, WatchTargetType } from "@taskflow/contracts";
import { useState } from "react";

import { errorMessage } from "@taskflow/client-core";
import {
  DEFAULT_WATCH_EVENTS,
  WATCH_EVENT_LABELS,
  WATCH_EVENTS,
  WATCH_TARGET_LABELS,
  type WatchItem,
  useRemoveWatchMutation,
  useSetWatchMutation,
  useWatchListQuery,
} from "@taskflow/client-core/watching";
import { Button, Checkbox, Modal, toast } from "@/shared/ui";

export interface WatchTargetInfo {
  targetType: WatchTargetType;
  targetId: string;
  /** Название объекта — в заголовке диалога. */
  name: string;
}

export function WatchDialog({
  target,
  current,
  onClose,
}: {
  target: WatchTargetInfo;
  /** Действующая подписка; нет — диалог её оформляет. */
  current: WatchItem | undefined;
  onClose: () => void;
}) {
  const list = useWatchListQuery();
  const [setWatch, setState] = useSetWatchMutation();
  const [removeWatch, removeState] = useRemoveWatchMutation();
  const [events, setEvents] = useState<WatchEvent[]>(
    current?.events ?? list.data?.defaultEvents ?? DEFAULT_WATCH_EVENTS,
  );
  const [error, setError] = useState<string | null>(null);
  const busy = setState.isLoading || removeState.isLoading;
  const what = `${WATCH_TARGET_LABELS[target.targetType]} «${target.name}»`;

  const toggle = (event: WatchEvent, on: boolean) =>
    setEvents((items) =>
      on ? WATCH_EVENTS.filter((item) => item === event || items.includes(item)) : items.filter((item) => item !== event),
    );

  const save = async () => {
    if (events.length === 0) {
      setError("Выберите хотя бы одно событие — или прекратите наблюдение");
      return;
    }
    setError(null);
    try {
      await setWatch({ targetType: target.targetType, targetId: target.targetId, events }).unwrap();
      toast.success(current === undefined ? "Вы наблюдатель" : "Настройки сохранены", `Наблюдение за ${what}`);
      onClose();
    } catch (raw) {
      setError(errorMessage(raw));
    }
  };

  const stop = async () => {
    setError(null);
    try {
      await removeWatch({ targetType: target.targetType, targetId: target.targetId }).unwrap();
      toast.success("Наблюдение прекращено", `Больше не наблюдаете за ${what}`);
      onClose();
    } catch (raw) {
      setError(errorMessage(raw));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={current === undefined ? `Наблюдать за ${what}` : `Вы наблюдатель: ${what}`}
      width="sm"
      dismissable={!busy}
      footer={
        <>
          {current !== undefined && (
            <Button variant="danger" onClick={() => void stop()} loading={removeState.isLoading} disabled={busy}>
              Прекратить наблюдение
            </Button>
          )}
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={() => void save()} loading={setState.isLoading} disabled={busy}>
            {current === undefined ? "Наблюдать" : "Сохранить"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[13px] text-muted">
          Уведомления придут о событиях с существующими и новыми задачами — с этого момента. Каждое
          уведомление открывает задачу. Прав наблюдение не добавляет.
        </p>
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-[12px] font-bold text-fg">Какие уведомления получать</legend>
          {WATCH_EVENTS.map((event) => (
            <Checkbox
              key={event}
              label={WATCH_EVENT_LABELS[event]}
              checked={events.includes(event)}
              disabled={busy}
              onChange={(on) => toggle(event, on)}
            />
          ))}
        </fieldset>
        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
